import { logger } from '../utils/core/logger';

export interface NetworkAdapter {
    // NOTE: widget chat sends do NOT go through post() — they stream via raw
    // fetch() to /api/lightrag/query/stream (inboundx.ts). post() carries only
    // legacy-n8n + analytics proxy calls. Wrapping post() will NOT observe sends.
    post(url: string, data: unknown): Promise<unknown>;
}

type ChromeRuntimeError = {
    message?: string;
};

type ChromeMessage = {
    id?: string;
    success?: boolean;
    response?: unknown;
    error?: string;
};

type ChromeMessageListener = (
    message: ChromeMessage,
    sender: unknown,
    sendResponse: (response?: unknown) => void
) => void;

type ChromeRuntimeApi = {
    lastError?: ChromeRuntimeError;
    sendMessage(message: unknown, responseCallback?: (response: unknown) => void): void;
    onMessage: {
        addListener(listener: ChromeMessageListener): void;
        removeListener(listener: ChromeMessageListener): void;
    };
};

type ChromeApi = {
    runtime?: ChromeRuntimeApi;
};

function getChromeRuntime(): ChromeRuntimeApi {
    const chromeApi = typeof chrome === 'undefined' ? undefined : (chrome as ChromeApi);
    if (!chromeApi?.runtime) {
        throw new Error('Chrome runtime not available');
    }
    return chromeApi.runtime;
}

function isAbortError(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        error.name === 'AbortError'
    );
}

export class FetchNetworkAdapter implements NetworkAdapter {
    async post(url: string, data: unknown): Promise<unknown> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Unknown error');
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            return response.json();
        } catch (error) {
            clearTimeout(timeoutId);
            if (isAbortError(error)) {
                throw new Error('Network request timeout');
            }
            throw error;
        }
    }
}

export class ChromeNetworkAdapter implements NetworkAdapter {
    async post(url: string, data: unknown): Promise<unknown> {
        const runtime = getChromeRuntime();

        logger.debug('ChromeNetworkAdapter: Chrome runtime check passed', {
            chrome: typeof chrome,
            runtime: typeof runtime,
            sendMessage: typeof runtime.sendMessage,
            onMessage: typeof runtime.onMessage,
        });

        return new Promise((resolve, reject) => {
            const internalTrackingId = `net_${Date.now()}_${Math.random()}`;
            let isResolved = false;

            logger.debug('ChromeNetworkAdapter: Starting request', {
                internalTrackingId,
                url,
                data,
            });

            // Test service worker availability with a simple ping first
            logger.debug('ChromeNetworkAdapter: Testing service worker with ping...');
            runtime.sendMessage({ action: 'ping', test: true }, (pingResponse: unknown) => {
                if (runtime.lastError) {
                    console.error(
                        'ChromeNetworkAdapter: Ping failed - service worker not responding:',
                        runtime.lastError
                    );
                } else {
                    logger.debug(
                        'ChromeNetworkAdapter: Ping successful, service worker is active:',
                        pingResponse
                    );
                }
            });

            const messageHandler: ChromeMessageListener = (message, _sender, _sendResponse) => {
                logger.debug('ChromeNetworkAdapter: Received message', {
                    message,
                    internalTrackingId,
                });

                if (message.id === internalTrackingId) {
                    logger.debug('ChromeNetworkAdapter: Message ID matches, processing response', {
                        message,
                    });

                    if (isResolved) {
                        console.warn(
                            'ChromeNetworkAdapter: Already resolved, ignoring duplicate response'
                        );
                        return; // Prevent double resolution
                    }
                    isResolved = true;

                    runtime.onMessage.removeListener(messageHandler);
                    clearTimeout(timeoutId);

                    if (message.success) {
                        logger.debug('ChromeNetworkAdapter: Success response', {
                            response: message.response,
                        });
                        resolve(message.response);
                    } else {
                        console.error('ChromeNetworkAdapter: Error response', {
                            error: message.error,
                        });
                        reject(new Error(message.error || 'Unknown error'));
                    }
                }
            };

            // Set up timeout to prevent hanging indefinitely
            const timeoutId = setTimeout(() => {
                if (isResolved) {
                    logger.debug('ChromeNetworkAdapter: Timeout triggered but already resolved');
                    return; // Prevent double resolution
                }
                console.error('ChromeNetworkAdapter: Request timeout after 35 seconds', {
                    internalTrackingId,
                    url,
                });
                isResolved = true;

                runtime.onMessage.removeListener(messageHandler);
                reject(new Error('Chrome network request timeout'));
            }, 35000);

            runtime.onMessage.addListener(messageHandler);

            logger.debug('ChromeNetworkAdapter: Sending message to background script', {
                action: 'callWebhook',
                id: internalTrackingId,
                url,
                data,
            });

            runtime.sendMessage(
                {
                    action: 'callWebhook',
                    id: internalTrackingId,
                    url,
                    data,
                },
                (response: unknown) => {
                    logger.debug('ChromeNetworkAdapter: sendMessage callback received', {
                        response,
                    });
                    if (runtime.lastError) {
                        console.error(
                            'ChromeNetworkAdapter: sendMessage error:',
                            runtime.lastError
                        );
                    }
                }
            );
        });
    }
}
